import React, { useContext, useState, useEffect, useCallback } from 'react';
import { FaRegCircleCheck } from "react-icons/fa6";
import { IoCloseCircleOutline } from "react-icons/io5";
import axios from "axios";
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { showToast } from "../../Components/utils/ShowToast";
import { LoginContext } from "../../Contexts/LoginContext";
import { calculateTax, formatCurrency, formatCurrencyDual } from '../../data/constants';
import Loader from "../../Components/utils/Loader";

/** Promo valid through end of expiry calendar day (UTC) — matches backend. */
function isDiscountPeriodActive(expiryDate) {
    if (!expiryDate) return false;
    const d = new Date(expiryDate);
    if (Number.isNaN(d.getTime())) return false;
    const endOfDayUtc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));
    return Date.now() <= endOfDayUtc.getTime();
}

function Upgrade() {
    const { user, fetchFees, fetchUserData, currency, exchangeRate } = useContext(LoginContext);
    const stripe = useStripe();
    const elements = useElements();
    const [membership, setMembership] = useState(null);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [selectedMonths, setSelectedMonths] = useState(3);
    const [discountedPrice, setDiscountedPrice] = useState(0);
    const [discountPercent, setDiscountPercent] = useState(null);
    const [discountExpiry, setDiscountExpiry] = useState(null);
    const [showCheckoutModel, setShowCheckoutModel] = useState(false);
    const [tax, setTax] = useState(0);
    const token = localStorage.getItem("token");
    const [loading, setLoading] = useState(true);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelDetails, setCancelDetails] = useState('');
    const [cancelSubmitting, setCancelSubmitting] = useState(false);
    const [buyerBalance, setBuyerBalance] = useState(0);
    const [applyCredits, setApplyCredits] = useState(true);
    const [upgradeLoading, setUpgradeLoading] = useState(false);

    useEffect(() => {
        if (!user) return;

        const fetchData = async () => {
            try {
                const membershipResponse = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/feesAndMembership`);
                if (membershipResponse.data.success) {
                    setMembership(membershipResponse.data.membership);
                }

                const planResponse = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/seller/current`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (planResponse.data.success) {
                    setCurrentPlan(planResponse.data.seller.plan || null);
                }

                // Fetch account credit balance
                const balanceRes = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/buyer/balance`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setBuyerBalance(balanceRes.data?.balance ?? 0);
            } catch (e) {
                showToast(e?.response?.data?.error || "Something went wrong!", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user, token]);

    const calculateDiscountedPrice = useCallback((months, mem) => {
        if (!mem) return;

        const plan = months === 3 ? "threeMonths" : months === 6 ? "sixMonths" : "twelveMonths";
        let price = mem[plan] || 0;

        let applyDiscount = false;
        if (mem.offerDiscount) {
            if (mem.discountType === 'onAllPlans' && mem.discounts?.allPlans?.discount) {
                applyDiscount = isDiscountPeriodActive(mem.discounts.allPlans.expiryDate);
                if (applyDiscount) {
                    price -= price * (mem.discounts.allPlans.discount / 100);
                    setDiscountPercent(mem.discounts.allPlans.discount);
                    setDiscountExpiry(mem.discounts.allPlans.expiryDate);
                }
            } else if (mem.discountType === 'individualDiscount' && mem.discounts?.[plan]?.discount) {
                const slot = mem.discounts[plan];
                applyDiscount = isDiscountPeriodActive(slot.expiryDate);
                if (applyDiscount) {
                    price -= price * (slot.discount / 100);
                    setDiscountPercent(slot.discount);
                    setDiscountExpiry(slot.expiryDate);
                }
            }
        }

        if (!applyDiscount) {
            setDiscountPercent(null);
            setDiscountExpiry(null);
        }

        setDiscountedPrice(price);
    }, []);

    useEffect(() => {
        if (membership) {
            calculateDiscountedPrice(selectedMonths, membership);
        }
    }, [selectedMonths, membership, calculateDiscountedPrice]);

    useEffect(() => {
        if (user && discountedPrice) {
            setTax(calculateTax(discountedPrice, user?.sellerId?.sellerAddress?.country?.name, user?.sellerId?.sellerAddress?.state?.name));
        }
    }, [discountedPrice, user]);

    const handlePlanChange = (months) => {
        setSelectedMonths(months);
    };

    // ─── Derived credit values ───────────────────────────────────
    const totalToPay = discountedPrice + tax;
    const creditToApply = applyCredits && buyerBalance > 0 && totalToPay > 0
        ? Math.min(buyerBalance, totalToPay)
        : 0;
    const cardAmountDue = Math.max(0, totalToPay - creditToApply);
    const paysWithCreditsOnly = creditToApply >= totalToPay && totalToPay > 0;

    const handleUpgrade = async () => {
        if (!paysWithCreditsOnly) {
            // Need Stripe
            if (!stripe || !elements) { showToast('Stripe is not loaded', 'error'); return; }
            const cardElement = elements.getElement(CardNumberElement);
            if (!cardElement) { showToast('Please enter card details', 'error'); return; }
        }
        setUpgradeLoading(true);
        try {
            // Only the plan length is sent — the server prices the plan and sets
            // its dates itself, and returns the figures it settled on.
            const planData = { months: selectedMonths, paymentMethod: 'stripe' };

            const { data } = await axios.put(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/plan/upgrade/${user?.sellerId?._id}`,
                { plan: planData, creditAmount: creditToApply },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (data.fullCredits) {
                // Fully paid by credits — plan already activated on backend
                setCurrentPlan(data.seller?.plan || null);
                setBuyerBalance(prev => Math.max(0, prev - (data.appliedCreditAmount || creditToApply)));
                setShowCheckoutModel(false);
                fetchUserData();
                fetchFees(user);
                showToast('Membership activated using account credits!', 'success');
                return;
            }

            // Stripe payment required for remaining amount
            const cardElement = elements.getElement(CardNumberElement);
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({ type: 'card', card: cardElement });
            if (pmError) { showToast(pmError.message, 'error'); setUpgradeLoading(false); return; }

            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
                payment_method: paymentMethod.id,
            });
            if (confirmError) { showToast(confirmError.message, 'error'); setUpgradeLoading(false); return; }

            if (paymentIntent.status === 'succeeded') {
                // The plan is granted server-side only after this call verifies
                // the payment with Stripe. Skipping it leaves the seller on the
                // free tier with the charge already taken, so surface failures.
                const { data: confirmed } = await axios.put(
                    `${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/plan/confirm/${user?.sellerId?._id}`,
                    { paymentIntentId: paymentIntent.id },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                setCurrentPlan(confirmed.seller?.plan || null);
                setBuyerBalance(prev => Math.max(0, prev - (confirmed.appliedCreditAmount || 0)));
                setShowCheckoutModel(false);
                fetchUserData();
                fetchFees(user);
                showToast('Membership upgraded successfully!', 'success');
            }
        } catch (err) {
            showToast(err?.response?.data?.error || err?.message || 'Payment failed', 'error');
        } finally {
            setUpgradeLoading(false);
        }
    };

    const openCancelModal = () => {
        setCancelReason('');
        setCancelDetails('');
        setCancelModalOpen(true);
    };

    const handleConfirmCancelMembership = () => {
        if (!cancelReason) {
            showToast('Please select a reason', 'error');
            return;
        }
        const parts = [cancelReason];
        if (cancelDetails.trim()) parts.push(cancelDetails.trim());
        const reason = parts.join(' — ');

        setCancelSubmitting(true);
        axios.put(
            `${process.env.REACT_APP_Backend_Host_Name}/api/v1/sellers/plan/cancel/${user?.sellerId?._id}`,
            { reason },
            { headers: { Authorization: `Bearer ${token}` } }
        )
            .then((response) => {
                if (response.data.success) {
                    setCurrentPlan(null);
                    setCancelModalOpen(false);
                    showToast('Your Premium membership has ended.', 'success');
                    fetchUserData();
                    fetchFees(user);
                }
            })
            .catch((e) => {
                showToast(e?.response?.data?.error || e?.response?.data?.message || 'Something went wrong!', 'error');
            })
            .finally(() => setCancelSubmitting(false));
    };

    const formatDateTime = (date) => {
        const options = { month: 'short', day: 'numeric', year: 'numeric' };
        return new Date(date).toLocaleDateString(undefined, options);
    };

    return (
        <div className='upgradeDiv'>
            {loading && <Loader type="checkmate" />}
            <section className="section">
                <div className="upgradeContent">
                    <h1 className="primaryHeading"><span>UPGRADE</span> YOUR ACCOUNT</h1>

                    {currentPlan && (
                        <div className="upgradeMembershipCard">
                            <p className="upgradeMembershipCardLabel">Premium membership</p>
                            <h2 className="upgradeMembershipCardTitle">You have full access to Premium benefits</h2>
                            <p className="upgradeMembershipCardText">
                                Your plan includes trade leads, lower selling fees, and boosted visibility. Access remains active until{' '}
                                <strong>{formatDateTime(currentPlan.endDate)}</strong>.
                            </p>
                            <ul className="upgradeMembershipCardList">
                                <li>Manage your subscription anytime from this page.</li>
                                <li>After the end date above, your account returns to the Basic plan unless you renew.</li>
                            </ul>
                            <div className="upgradeMembershipCardActions">
                                <button type="button" className="modalButton cancel" onClick={openCancelModal}>
                                    Cancel membership
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="timeLine">
                        <div className='checkboxDiv'>
                            <input type="radio" id="threeMonths" className='checkbox' name='planTimeline' checked={selectedMonths === 3} onChange={() => handlePlanChange(3)} />
                            <label htmlFor="threeMonths">3 Months</label>
                        </div>
                        <div className='checkboxDiv'>
                            <input type="radio" id="sixMonths" className='checkbox' name='planTimeline' checked={selectedMonths === 6} onChange={() => handlePlanChange(6)} />
                            <label htmlFor="sixMonths">6 Months</label>
                        </div>
                        <div className='checkboxDiv'>
                            <input type="radio" id="twelveMonths" className='checkbox' name='planTimeline' checked={selectedMonths === 12} onChange={() => handlePlanChange(12)} />
                            <label htmlFor="twelveMonths">12 Months</label>
                        </div>
                    </div>
                    <div className="plans">
                        <div className="freePlan plan">
                            <h2 className="secondaryHeading">Basic</h2>
                            <div className="price">{formatCurrency(0, currency, exchangeRate)}</div>
                            <div className="rows">
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Streamlined Business Tools</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Post Products to Sell</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Bookmark Favorite Sellers</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Set Preferred Delivery or Pickup</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Access to Blogs & Articles</span>
                                </div>
                                <div className="row notIncluded">
                                    <IoCloseCircleOutline className='icon' />
                                    <span>Access to Tradelead Section</span>
                                </div>
                                <div className="row notIncluded">
                                    <IoCloseCircleOutline className='icon' />
                                    <span>Less Deduction on Product Sales</span>
                                </div>
                                <div className="row notIncluded">
                                    <IoCloseCircleOutline className='icon' />
                                    <span>Product Listings on Top of Pages</span>
                                </div>
                                <div className="row notIncluded">
                                    <IoCloseCircleOutline className='icon' />
                                    <span>Option to Feature in “Top Sellers” Section</span>
                                </div>
                                <div className="row notIncluded">
                                    <IoCloseCircleOutline className='icon' />
                                    <span>Priority Visibility in Search Results</span>
                                </div>
                            </div>
                        </div>
                        <div className="premiumPlan plan">
                            <h2 className="secondaryHeading">Premium</h2>
                            <div className="price">{formatCurrency((discountedPrice / selectedMonths), currency, exchangeRate)} <span>/ month</span></div>
                            {discountPercent != null && <div className="discountPercent">{discountPercent}% Discount</div>}
                            <div className="rows">
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Streamlined Business Tools</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Post Products to Sell</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Bookmark Favorite Sellers</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Set Preferred Delivery or Pickup</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Access to Blogs & Articles</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Access to Tradelead Section</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Less Deduction on Product Sales</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Product Listings on Top of Pages</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Option to Feature in “Top Sellers” Section</span>
                                </div>
                                <div className="row">
                                    <FaRegCircleCheck className='icon' />
                                    <span>Priority Visibility in Search Results</span>
                                </div>
                            </div>
                            <div className="buttonDiv">
                                <button
                                    className='modalButton submit'
                                    onClick={() => setShowCheckoutModel(true)}
                                    disabled={currentPlan}
                                >
                                    {currentPlan && (currentPlan.months === selectedMonths) ? "Already Using" : "Buy Now"}
                                </button>
                            </div>
                            {discountExpiry && discountPercent != null && (
                                <div className="discountExpiry">Discount Ending On - <span>{formatDateTime(discountExpiry)}</span></div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
            {/* Custom upgrade payment modal */}
            {showCheckoutModel && (
                <div className="modalOverlay paymentModelOverlayDiv">
                    <div className="modalContent">
                        <div className="form">
                            <h2 className="primaryHeadingSmall"><span>Upgrade Account</span></h2>
                            <div className="horizontalLine"></div>
                            <div className='row'>
                                <span>Plan price</span>
                                <strong>{formatCurrency(discountedPrice, currency, exchangeRate)}</strong>
                            </div>
                            <div className='row'>
                                <span>Tax</span>
                                <strong>{formatCurrency(tax, currency, exchangeRate)}</strong>
                            </div>
                            <div className='row'>
                                <span>Total</span>
                                <strong>{formatCurrency(totalToPay, currency, exchangeRate)}</strong>
                            </div>

                            {buyerBalance > 0 && (
                                <div style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                                    <label className="checkoutCreditsApplyRow">
                                        <input
                                            type="checkbox"
                                            checked={applyCredits}
                                            onChange={(e) => setApplyCredits(e.target.checked)}
                                        />
                                        <span>
                                            Apply account credits ({formatCurrencyDual(buyerBalance, currency, exchangeRate)} available)
                                            {applyCredits && creditToApply > 0 && (
                                                <span className="checkoutCreditsApplyHint">
                                                    {' '}— {formatCurrencyDual(creditToApply, currency, exchangeRate)} will be used toward this upgrade
                                                </span>
                                            )}
                                        </span>
                                    </label>

                                    {applyCredits && creditToApply > 0 && cardAmountDue > 0 && (
                                        <p className="checkoutCreditsSplitNote">
                                            The rest ({formatCurrencyDual(cardAmountDue, currency, exchangeRate)}) will be charged to your card.
                                        </p>
                                    )}
                                    {applyCredits && paysWithCreditsOnly && (
                                        <p className="checkoutCreditsOnlyNote">
                                            Your membership will be covered entirely from account credits. No card charge.
                                        </p>
                                    )}
                                </div>
                            )}

                            {!paysWithCreditsOnly && (
                                <div className="checkoutStripeFields">
                                    <div className="checkoutStripeRow">
                                        <label>Card Number</label>
                                        <CardNumberElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                                    </div>
                                    <div className="checkoutStripeRowGroup">
                                        <div className="checkoutStripeRow">
                                            <label>Expiry</label>
                                            <CardExpiryElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                                        </div>
                                        <div className="checkoutStripeRow">
                                            <label>CVC</label>
                                            <CardCvcElement className="inputField checkoutStripeElement" options={{ style: { base: { fontSize: '16px' } } }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="buttonsDiv">
                            <button
                                className="modalButton cancel"
                                onClick={() => setShowCheckoutModel(false)}
                                disabled={upgradeLoading}
                            >
                                Close
                            </button>
                            <button
                                className="modalButton submit"
                                onClick={handleUpgrade}
                                disabled={upgradeLoading}
                            >
                                {upgradeLoading ? <Loader type="miniPlus" /> : (
                                    paysWithCreditsOnly
                                        ? 'Activate with credits'
                                        : `Pay ${formatCurrency(cardAmountDue, currency, exchangeRate)}`
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {cancelModalOpen && (
                <div
                    className="modalOverlay paymentModelOverlayDiv upgradeCancelModalOverlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="upgradeCancelTitle"
                    onClick={() => !cancelSubmitting && setCancelModalOpen(false)}
                >
                    <div className="modalContent upgradeCancelModal" onClick={(e) => e.stopPropagation()}>
                        <h2 id="upgradeCancelTitle" className="primaryHeadingSmall"><span>End Premium membership?</span></h2>
                        <p className="upgradeCancelModalIntro">
                            If you continue, your Premium access and benefits (including trade leads and reduced selling fees) will end immediately and your account will return to the Basic plan. You can upgrade again anytime.
                        </p>
                        <div className="inputDiv">
                            <label htmlFor="cancelReason">Why are you leaving?</label>
                            <select
                                id="cancelReason"
                                className="inputField"
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                disabled={cancelSubmitting}
                            >
                                <option value="">Select a reason…</option>
                                <option value="Too expensive for my business">Too expensive for my business</option>
                                <option value="Not using Premium features">Not using Premium features</option>
                                <option value="Switching to another platform">Switching to another platform</option>
                                <option value="Taking a break from selling">Taking a break from selling</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="inputDiv">
                            <label htmlFor="cancelDetails">Additional details <span className="upgradeCancelOptional">(optional)</span></label>
                            <textarea
                                id="cancelDetails"
                                className="inputField upgradeCancelTextarea"
                                rows={3}
                                value={cancelDetails}
                                onChange={(e) => setCancelDetails(e.target.value)}
                                disabled={cancelSubmitting}
                                placeholder="Help us improve (optional)"
                            />
                        </div>
                        <div className="buttonsDiv">
                            <button
                                type="button"
                                className="modalButton cancel"
                                onClick={() => !cancelSubmitting && setCancelModalOpen(false)}
                                disabled={cancelSubmitting}
                            >
                                Keep membership
                            </button>
                            <button
                                type="button"
                                className="modalButton submit"
                                onClick={handleConfirmCancelMembership}
                                disabled={cancelSubmitting}
                            >
                                {cancelSubmitting ? <Loader type="miniPlus" /> : 'End membership'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Upgrade;
